import React, { useState } from "react";
import { Button, Divider, Dropdown, Menu, message } from "antd";
import { MoreOutlined, HolderOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  useBulletinSectionService,
  useBulletinContentService,
} from "../../../services";
import {
  prepareBlockDataForBackend,
  transformBackendDataForFrontend,
} from "../../../utils/blockDataUtils";
import "../../../styles/bulletinAdmin/components/bulletinSection.scss";
import BlockDropZone from "../BlockDropZone";
import ConfirmDeleteModal from "../modals/EbulletinConfirmDelete";
import ReorderControl from "../EbulletinReorderControl";

export default function SectionCard({
  id,
  section,
  sectionIdx,
  sections,
  setSections,
  bulletinId,
  reloadSections = () => Promise.resolve(),
  isAdmin,
  isReordering,
}) {
  const [messageApi, contextHolder] = message.useMessage();
  const { service: sectionService } = useBulletinSectionService();
  const { service: contentService } = useBulletinContentService();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(!!section?.isEditing);

  const blocksAreEditable = isAdmin && isEditing;

  // Ensure section has a stable ID for reordering
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: !isReordering });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!section) return null;
  if (!Array.isArray(section.blocks)) section.blocks = [];

  if (section.blocks.length === 0) {
    section.blocks = [
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        type: "text",
        content: "",
      },
    ];
  }

  // Handle title change for the section
  const handleTitleChange = (e) => {
    const updated = [...sections];
    updated[sectionIdx].title = e.target.value;
    setSections(updated);
  };

  // Prepare block data for backend
  const handleSave = async () => {
    if (!section.title?.trim()) {
      messageApi.open({ type: "error", content: "Missing section title" });
      return;
    }

    const hideLoading = messageApi.open({
      type: "loading",
      content: "Saving section...",
      duration: 0,
    });

    try {
      const adminId = 1;
      const payload = { section_title: section.title, admin_id: adminId };
      const isTemporary = !section.id || typeof section.id !== "number";

      // Save or update section first
      let sectionResponse = isTemporary
        ? await sectionService.createSection(bulletinId, payload)
        : await sectionService.updateSection(bulletinId, section.id, payload);

      const savedSection = sectionResponse.data;
      const sectionId = savedSection.id;

      // Prepare blocks for saving with proper validation
      const blocksToSave = section.blocks
        .filter((block) => block && block.type) // Filter out invalid blocks
        .map((block, index) => {
          // Clean the block data and ensure required fields
          const cleanBlock = {
            ...block,
            display_order: index,
          };

          // Remove frontend-only fields that cause backend validation issues
          delete cleanBlock.id;
          delete cleanBlock.contentId;
          delete cleanBlock.created_at;
          delete cleanBlock.updated_at;
          delete cleanBlock.is_active;

          return prepareBlockDataForBackend(cleanBlock);
        });

      // Validate that we have blocks to save
      if (blocksToSave.length === 0) {
        // Add a default text block if no blocks exist
        blocksToSave.push({
          type: "text",
          content: "",
          display_order: 0,
        });
      }

      // Prepare the bulk payload for content update
      const bulkPayload = {
        blocks: blocksToSave,
        admin_id: adminId,
      };

      console.log("Saving blocks:", JSON.stringify(bulkPayload, null, 2));

      // Make the bulk update API call
      const contentResponse = await contentService.updateBulk(
        bulletinId,
        sectionId,
        bulkPayload
      );

      // Transform the response data for frontend use
      const savedBlocks = (contentResponse.data || []).map(
        transformBackendDataForFrontend
      );

      // Update the sections state
      const updated = [...sections];
      updated[sectionIdx] = {
        ...savedSection,
        stableId: section.stableId,
        title: savedSection.section_title,
        blocks: savedBlocks,
        display_order: section.display_order,
      };

      // Clean up temporary flags
      delete updated[sectionIdx].tempId;
      delete updated[sectionIdx].isEditing;

      setSections(updated);
      setIsEditing(false);

      hideLoading();
      messageApi.open({
        type: "success",
        content: "The section and content is saved successfully",
      });
    } catch (err) {
      console.error("Save error:", err);

      // Enhanced error logging for debugging
      if (err.response?.status === 422) {
        console.error("Validation error details:", err.response.data);
      }

      hideLoading();

      // Show more specific error message
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save section";

      messageApi.open({
        type: "error",
        content: `Save failed: ${errorMessage}`,
      });
    }
  };

  const handleDelete = async () => {
    setConfirmLoading(true);
    try {
      if (section.id && section.id > 0) {
        await sectionService.deleteSection(bulletinId, section.id);
      }
      await reloadSections();
      messageApi.open({ type: "success", content: "Section deleted!" });
    } catch (err) {
      messageApi.open({ type: "error", content: "Failed to delete section." });
    } finally {
      setConfirmLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Handle block deletion
  const handleDeleteBlock = async (blockIdx) => {
    try {
      const updated = [...sections];
      const block = updated[sectionIdx].blocks[blockIdx];

      if (block.contentId) {
        await contentService.deleteContent(
          bulletinId,
          section.id,
          block.contentId
        );
      }

      updated[sectionIdx].blocks.splice(blockIdx, 1);
      setSections(updated);
      messageApi.open({
        type: "success",
        content: "Content block deleted successfully",
      });
    } catch (err) {
      console.error("Delete block error:", err);
      messageApi.open({
        type: "error",
        content: "Failed to delete content block",
      });
    }
  };

  // Handle inserting a new block
  const handleInsertBlock = (blockI, type = "paragraph") => {
    const updated = [...sections];
    const newBlock = {
      id: `temp-${Date.now()}`,
      type,
      content: "",
      style: {},
    };
    updated[sectionIdx].blocks.splice(blockI + 1, 0, newBlock);
    setSections(updated);
  };

  // Handle reordering blocks
  const handleBlockReorder = (reorderedBlocks) => {
    const updated = [...sections];
    updated[sectionIdx].blocks = reorderedBlocks;
    setSections(updated);
  };

  const moreMenu = (
    <Menu
      items={[
        {
          key: "delete",
          label: "Delete",
          onClick: () => setShowDeleteModal(true),
        },
      ]}
    />
  );

  const cardClassName = `section-card ${!isAdmin ? "view-only" : ""} ${
    isReordering ? "reordering" : ""
  }`;

  return (
    <>
      {contextHolder}
      <div ref={setNodeRef} style={style} className={cardClassName}>
        <div className="section-header">
          <div className="left">
            {isAdmin && isReordering && (
              <div {...attributes} {...listeners} className="drag-handle">
                <HolderOutlined />
              </div>
            )}
            {blocksAreEditable ? (
              <input
                placeholder="Section Title"
                value={section.title || ""}
                onChange={handleTitleChange}
                disabled={isReordering}
              />
            ) : (
              <div
                className={
                  isAdmin
                    ? "section-title-display admin"
                    : "section-title-display view-only"
                }
              >
                {section.title}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="actions">
              {!isReordering ? (
                <>
                  {isEditing ? (
                    <>
                      <Button
                        onClick={handleSave}
                        className="bg-maroon text-white"
                      >
                        Save
                      </Button>
                      <Dropdown overlay={moreMenu}>
                        <Button type="text" icon={<MoreOutlined />} />
                      </Dropdown>
                    </>
                  ) : (
                    <Button onClick={() => setIsEditing(true)}>Edit</Button>
                  )}
                </>
              ) : (
                <ReorderControl
                  section={section}
                  sectionIdx={sectionIdx}
                  sections={sections}
                  setSections={setSections}
                  reloadSections={reloadSections}
                  bulletinId={bulletinId}
                />
              )}
            </div>
          )}
        </div>

        {/* --- Collapse section body when reordering and Show for non-admins --- */}
        {(!isReordering && isAdmin) || !isAdmin ? (
          <>
            {isAdmin && !isReordering && <Divider className="bg-maroon" />}
            <BlockDropZone
              blocks={section.blocks}
              onReorder={handleBlockReorder}
              isEditMode={blocksAreEditable}
              sectionIdx={sectionIdx}
              sections={sections}
              setSections={setSections}
              handleInsertBlock={handleInsertBlock}
              handleDeleteBlock={handleDeleteBlock}
            />
          </>
        ) : null}

        <ConfirmDeleteModal
          type="section"
          open={showDeleteModal}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          confirmLoading={confirmLoading}
        />
      </div>
    </>
  );
}
